import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@mondy/shared/api-client';
import { api } from '@/api';
import { AdminNav } from '@/components/admin/AdminNav';

type SeedKind =
  | 'all'
  | 'teams'
  | 'fifa-rankings'
  | 'fixtures'
  | 'stadiums'
  | 'api-football-fixtures'
  | 'events'
  | 'events-force'
  | 'reset';

interface SeedAllResponse {
  seed: { kind: 'all'; teams: number; matches: number; stadiums: number };
}
interface SeedTeamsResponse {
  seed: { kind: 'teams'; count: number };
}
interface SeedFifaRankingsResponse {
  seed: { kind: 'fifa-rankings'; count: number; updated: number };
}
interface SeedFixturesResponse {
  seed: { kind: 'fixtures'; count: number };
}
interface SeedStadiumsResponse {
  seed: { kind: 'stadiums'; count: number; linked: number; unlinked: number };
}
interface SeedApiFootballFixturesResponse {
  seed: {
    kind: 'api-football-fixtures';
    ran: boolean;
    mapped: number;
    alreadyMapped: number;
    unmatched: number;
    reason?: string;
  };
}
interface SeedEventsResponse {
  seed: { kind: 'events'; ran: boolean; updated: number; skipped: number; reason?: string };
}
interface ResetResponse {
  seed: {
    kind: 'reset';
    teams: number;
    stadiums: number;
    matches: number;
    predictions: number;
    caches: number;
    reminders: number;
  };
}

type SeedResponse =
  | SeedAllResponse
  | SeedTeamsResponse
  | SeedFifaRankingsResponse
  | SeedFixturesResponse
  | SeedStadiumsResponse
  | SeedApiFootballFixturesResponse
  | SeedEventsResponse
  | ResetResponse;

interface ActionMeta {
  kind: SeedKind;
  label: string;
  description: string;
  endpoint: string;
  /** Static request body for non-destructive actions (e.g. force flags). */
  body?: Record<string, unknown>;
  destructive?: boolean;
}

const ACTIONS: ActionMeta[] = [
  {
    kind: 'all',
    label: 'Seed (teams + fixtures)',
    description:
      'Idempotent upsert of every team and match from shared/data. Safe to re-run — won\u2019t overwrite manual match results.',
    endpoint: '/api/admin/seed',
  },
  {
    kind: 'teams',
    label: 'Seed teams only',
    description: 'Upsert teams from shared/data/teams.json. Leaves matches alone.',
    endpoint: '/api/admin/seed/teams',
  },
  {
    kind: 'fifa-rankings',
    label: 'Seed FIFA rankings',
    description:
      'Update every team’s FIFA world ranking from shared/data/fifa-rankings.json. Used as the final group-stage tiebreaker. Seed teams first.',
    endpoint: '/api/admin/seed/fifa-rankings',
  },
  {
    kind: 'fixtures',
    label: 'Seed fixtures only',
    description:
      'Upsert matches from shared/data/fixtures.json. Requires teams to already be seeded.',
    endpoint: '/api/admin/seed/fixtures',
  },
  {
    kind: 'stadiums',
    label: 'Seed stadiums',
    description:
      'Upsert stadiums from shared/data/stadiums.json and link every match to its stadium by venue. Requires fixtures to already be seeded.',
    endpoint: '/api/admin/seed/stadiums',
  },
  {
    kind: 'api-football-fixtures',
    label: 'Map API-Football fixtures',
    description:
      'Match each fixture to its API-Football fixture id (by kickoff day + team names) so lineups + formations can be synced. Needs API_FOOTBALL_KEY and teams/fixtures already seeded. Re-run as knockout teams resolve. Idempotent.',
    endpoint: '/api/admin/seed/api-football-fixtures',
  },
  {
    kind: 'events',
    label: 'Seed match events (missing only)',
    description:
      'Fetch in-match events (goals, cards, subs) from live-score-api for finished matches that have no events yet. Reads the live API — needs a configured API key. Idempotent.',
    endpoint: '/api/admin/seed/events',
  },
  {
    kind: 'events-force',
    label: 'Seed match events (all finished, refetch)',
    description:
      'Re-fetch events for every finished match, overwriting any existing events. Use to backfill after an event-mapping fix. Reads the live API — needs a configured API key.',
    endpoint: '/api/admin/seed/events',
    body: { force: true },
  },
  {
    kind: 'reset',
    label: 'Reset all data',
    description:
      'Wipes teams, matches, predictions, reminder logs, and system cache. Does NOT reseed — run the full seed afterwards. Type RESET to enable.',
    endpoint: '/api/admin/seed/reset',
    destructive: true,
  },
];

export function AdminSeedPage() {
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-5 p-4 md:p-8">
      <AdminNav />
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">Admin · Seed</h1>
        <p className="text-sm text-[color:var(--color-fg-muted)]">
          Apply <code>shared/data/</code> JSON to MongoDB after a deploy. Mirrors the{' '}
          <code>pnpm seed*</code> CLI scripts. All non-reset actions are idempotent.
        </p>
      </header>

      <div className="flex flex-col gap-3">
        {ACTIONS.filter((a) => !a.destructive).map((a) => (
          <SeedAction key={a.kind} action={a} />
        ))}
        <SquadSeedAction />
        {ACTIONS.filter((a) => a.destructive).map((a) => (
          <SeedAction key={a.kind} action={a} />
        ))}
      </div>
    </div>
  );
}

function SeedAction({ action }: { action: ActionMeta }) {
  const queryClient = useQueryClient();
  const [confirm, setConfirm] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: async () => {
      const body = action.destructive ? { confirm: 'RESET' } : action.body;
      return api.post<SeedResponse>(action.endpoint, body);
    },
    onSuccess: (data) => {
      const message = formatResult(data);
      // A 200 that did nothing useful (no API key, zero players returned) is a
      // soft failure — surface it in the danger style so it doesn't read as a
      // successful seed.
      if (isSoftFailure(data)) {
        setResult(null);
        setError(message);
      } else {
        setError(null);
        setResult(message);
      }
      setConfirm('');
      // Data has changed server-side — drop any admin/public caches that
      // might still reference the old records.
      void queryClient.invalidateQueries({ queryKey: ['admin'] });
      void queryClient.invalidateQueries({ queryKey: ['matches'] });
      void queryClient.invalidateQueries({ queryKey: ['match'] });
      void queryClient.invalidateQueries({ queryKey: ['teams'] });
      void queryClient.invalidateQueries({ queryKey: ['team'] });
      void queryClient.invalidateQueries({ queryKey: ['stadiums'] });
      void queryClient.invalidateQueries({ queryKey: ['stadium'] });
    },
    onError: (err) => {
      setResult(null);
      setError(err instanceof ApiError ? err.message : 'Request failed');
    },
  });

  const canRun = action.destructive ? confirm === 'RESET' && !run.isPending : !run.isPending;

  return (
    <div
      className={`flex flex-col gap-3 rounded-xl border p-4 ${
        action.destructive
          ? 'border-[color:var(--color-danger)] bg-[color:var(--color-bg-elevated)]'
          : 'border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]'
      }`}
    >
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold">{action.label}</p>
        <p className="text-xs text-[color:var(--color-fg-muted)]">{action.description}</p>
      </div>

      {action.destructive ? (
        <input
          type="text"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="Type RESET to confirm"
          className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-3 py-2 text-sm"
          autoComplete="off"
          spellCheck={false}
        />
      ) : null}

      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => run.mutate()}
          disabled={!canRun}
          className={`inline-flex h-10 items-center justify-center rounded-xl px-4 text-sm font-medium disabled:pointer-events-none disabled:opacity-50 ${
            action.destructive
              ? 'bg-[color:var(--color-danger)] text-white'
              : 'bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]'
          }`}
        >
          {run.isPending ? 'Running…' : action.destructive ? 'Reset' : 'Run'}
        </button>

        {result ? (
          <p className="text-right text-xs text-[color:var(--color-fg-muted)]">{result}</p>
        ) : null}
        {error ? (
          <p className="text-right text-xs text-[color:var(--color-danger)]">{error}</p>
        ) : null}
      </div>
    </div>
  );
}

interface SquadPlanTeam {
  teamId: string;
  fifaCode: string;
  name: string;
  apiTeamId: number;
}
interface SquadPlanResponse {
  seed: { kind: 'squads-plan'; ran: boolean; teams: SquadPlanTeam[]; unmatched: string[]; reason?: string };
}
interface SquadTeamResponse {
  seed: {
    kind: 'squads-team';
    ran: boolean;
    fifaCode: string;
    players: number;
    colored: number;
    kitColor: string | null;
    reason?: string;
  };
}

// Squad seed is multi-request (a plan call, then one call per team) so it can't
// use the generic single-POST SeedAction — it drives the loop client-side and
// shows live progress. The tab must stay open until all teams finish.
function SquadSeedAction() {
  const queryClient = useQueryClient();
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number; current: string } | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    setRunning(true);
    setSummary(null);
    setError(null);
    setProgress(null);
    try {
      const plan = await api.post<SquadPlanResponse>('/api/admin/seed/squads/plan');
      if (!plan.seed.ran) {
        setError('API-Football key not configured — nothing seeded.');
        return;
      }
      const teams = plan.seed.teams;
      if (teams.length === 0) {
        setError('No teams matched API-Football — check team names / aliases.');
        return;
      }

      let players = 0;
      let colored = 0;
      const failed: string[] = [];
      for (let i = 0; i < teams.length; i++) {
        const t = teams[i]!;
        setProgress({ done: i, total: teams.length, current: t.fifaCode });
        try {
          const r = await api.post<SquadTeamResponse>('/api/admin/seed/squads/team', {
            teamId: t.teamId,
            apiTeamId: t.apiTeamId,
            fifaCode: t.fifaCode,
          });
          players += r.seed.players;
          colored += r.seed.colored;
        } catch {
          failed.push(t.fifaCode);
        }
      }
      setProgress({ done: teams.length, total: teams.length, current: '' });

      const parts = [`Seeded ${players} players across ${teams.length} teams (${colored} colored).`];
      if (plan.seed.unmatched.length) parts.push(`Unmatched: ${plan.seed.unmatched.join(', ')}.`);
      if (failed.length) parts.push(`Failed: ${failed.join(', ')}.`);
      setSummary(parts.join(' '));

      void queryClient.invalidateQueries({ queryKey: ['teams'] });
      void queryClient.invalidateQueries({ queryKey: ['team'] });
      void queryClient.invalidateQueries({ queryKey: ['matches'] });
      void queryClient.invalidateQueries({ queryKey: ['match'] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Request failed');
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-4">
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold">Seed squads (players + kit colors)</p>
        <p className="text-xs text-[color:var(--color-fg-muted)]">
          Fetch every team&rsquo;s squad from API-Football (avatar, age, number, position) and compute
          each player&rsquo;s kit color from their avatar, plus each team&rsquo;s average. Needs
          API_FOOTBALL_KEY and teams already seeded. Runs team-by-team (a few minutes) — keep this tab
          open. Idempotent.
        </p>
      </div>

      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => void run()}
          disabled={running}
          className="inline-flex h-10 items-center justify-center rounded-xl bg-[color:var(--color-accent)] px-4 text-sm font-medium text-[color:var(--color-accent-fg)] disabled:pointer-events-none disabled:opacity-50"
        >
          {running ? 'Running…' : 'Run'}
        </button>

        <div className="text-right text-xs">
          {running && progress ? (
            <p className="text-[color:var(--color-fg-muted)]">
              {progress.done}/{progress.total} teams{progress.current ? ` — ${progress.current}` : ''}
            </p>
          ) : null}
          {summary ? <p className="text-[color:var(--color-fg-muted)]">{summary}</p> : null}
          {error ? <p className="text-[color:var(--color-danger)]">{error}</p> : null}
        </div>
      </div>
    </div>
  );
}

function formatResult(data: SeedResponse): string {
  const s = data.seed;
  switch (s.kind) {
    case 'all':
      return `Upserted ${s.teams} teams, ${s.matches} matches, and ${s.stadiums} stadiums.`;
    case 'teams':
      return `Upserted ${s.count} teams.`;
    case 'fifa-rankings':
      return `Applied FIFA rankings to ${s.updated} of ${s.count} teams.`;
    case 'fixtures':
      return `Upserted ${s.count} matches.`;
    case 'stadiums':
      return `Upserted ${s.count} stadiums — linked ${s.linked} matches${
        s.unlinked ? `, ${s.unlinked} unlinked` : ''
      }.`;
    case 'api-football-fixtures':
      if (!s.ran) return 'API-Football key not configured — nothing mapped.';
      return `Mapped ${s.mapped} fixture(s)${
        s.alreadyMapped ? `, ${s.alreadyMapped} already mapped` : ''
      }${s.unmatched ? `, ${s.unmatched} unmatched` : ''}.`;
    case 'events':
      if (!s.ran) return 'Live-score API key not configured — nothing fetched.';
      if (s.updated === 0)
        return `Fetched events for 0 matches (${s.skipped} skipped — already had events, no provider data, or manual results).`;
      return `Fetched events for ${s.updated} match(es), ${s.skipped} skipped.`;
    case 'reset':
      return `Wiped: ${s.teams} teams, ${s.stadiums} stadiums, ${s.matches} matches, ${s.predictions} predictions, ${s.reminders} reminders, ${s.caches} cache entries.`;
  }
}

// A fixture-mapping run that mapped nothing new and had nothing already mapped
// (key missing, teams unresolved, or every match unmatched) is reported as a
// warning rather than success.
function isSoftFailure(data: SeedResponse): boolean {
  if (data.seed.kind === 'api-football-fixtures') {
    return !data.seed.ran || (data.seed.mapped === 0 && data.seed.alreadyMapped === 0);
  }
  // For events, updated === 0 is a legitimate success (everything already had
  // events); only a missing API key is a soft failure.
  if (data.seed.kind === 'events') return !data.seed.ran;
  return false;
}
